import { useState, useEffect, useCallback } from 'react'
import {
  Search, Filter, MessageSquare, ChevronRight, Clock, User,
  Loader2, RefreshCw, Send, Sparkles, Edit3, Copy, Check,
  AlertCircle, X, FileText, Mail, ArrowUpRight, ArrowDownLeft,
  Bot, Tag, Zap
} from 'lucide-react'
import { getAuthToken } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'
import { API_BASE } from '@/services/api'
const GMAIL_TENANT_ID_OVERRIDE = import.meta.env.VITE_TENANT_ID || ''
const GMAIL_SYNC_QUERY = import.meta.env.VITE_GMAIL_SYNC_QUERY || ''

// Format date strings to user's local timezone in a consistent way
function formatDateLocal(dateStr: string | undefined | null): string {
  if (!dateStr) return 'Unknown date'
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return dateStr
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    })
  } catch {
    return dateStr
  }
}

// Retry configuration
const MAX_RETRIES = 2
const INITIAL_RETRY_DELAY = 3000 // 3 seconds
const REQUEST_TIMEOUT = 55000 // 55 seconds (Lambda timeout is 120s, backend takes ~30s currently)

// Utility function for fetch with timeout and retry
async function fetchWithRetry<T>(
  url: string,
  options: RequestInit & { retries?: number; retryDelay?: number } = {},
  onRetry?: (attempt: number, maxRetries: number) => void
): Promise<T> {
  const { retries = MAX_RETRIES, retryDelay = INITIAL_RETRY_DELAY, ...fetchOptions } = options

  let lastError: Error | null = null

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error ${response.status}`)
      }

      return await response.json()
    } catch (err) {
      clearTimeout(timeoutId)
      lastError = err instanceof Error ? err : new Error(String(err))

      // Check if it's a network error or timeout that we should retry
      const isRetryable =
        lastError.name === 'AbortError' ||
        lastError.message.includes('Failed to fetch') ||
        lastError.message.includes('NetworkError') ||
        lastError.message.includes('network') ||
        lastError.message.includes('timeout')

      if (attempt < retries && isRetryable) {
        const delay = retryDelay * Math.pow(2, attempt) // Exponential backoff
        console.log(`Retry attempt ${attempt + 1}/${retries} after ${delay}ms...`)
        onRetry?.(attempt + 1, retries)
        await new Promise(resolve => setTimeout(resolve, delay))
      } else if (!isRetryable) {
        // Non-retryable error, throw immediately
        throw lastError
      }
    }
  }

  throw lastError || new Error('Request failed after all retries')
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  metadata?: Record<string, any>
}

interface Draft {
  draft_id: string
  tenant_id: string
  session_id: string
  content: string
  category: string
  language: string
  status: 'pending' | 'sent' | 'edited' | 'generating' | 'ready'
  created_at: string
  updated_at: string
  metadata?: {
    model?: string
    usage?: Record<string, number>
    latency_ms?: number
    kb_sources?: string[]
    temperature?: number
    manual?: boolean
  }
}

interface Conversation {
  id: string
  session_id: string
  customer_email?: string
  customer_name?: string
  last_message?: string
  category: string
  created_at: string
  updated_at?: string
  status: 'active' | 'resolved' | 'escalated'
  message_count: number
  agent_name: string
  has_draft?: boolean
  needs_response?: boolean
}

interface ConversationDetail {
  conversation: Conversation
  messages: Message[]
}

interface AIDraftEmail {
  id: string
  message_id?: string
  from: string
  to: string
  subject: string
  body: string
  snippet?: string
  date: string
  direction: 'sent' | 'received'
}
 
interface AIDraftItem {
  draft_id: string
  message_id?: string
  from: string
  to: string
  subject: string
  body: string
  snippet?: string
  date: string
}

interface DraftVersion {
  draft_id: string
  thread_id: string
  tenant_id: string
  version_number: number
  content: {
    subject: string
    body: string
    to: string
    cc?: string
    bcc?: string
  }
  metadata: {
    is_ai_generated: boolean
    edited_by?: string
    edit_reason?: string
    agent_id?: string
    agent_name?: string
    model?: string
    original_draft_id?: string
  }
  status: 'draft' | 'edited' | 'sent'
  created_at: string
  sent_at?: string
}

interface AIDraftConversation {
  thread_id: string
  emails: AIDraftEmail[]
  drafts: AIDraftItem[]
  subject: string
  customer_email: string
  last_updated: string
  assigned_agent_id?: string | null
  assigned_agent_name?: string | null
  response_mode?: string | null  // "auto_reply", "template", or "manual"
  draft_response_enabled?: boolean | null
  draft_count?: number  // Draft count from API (available in lite mode)
}

// =============================================================================
// MODULE-LEVEL CACHE - persists across component mount/unmount (navigation)
// Eliminates redundant Gmail API calls when switching between sections
// =============================================================================
let _cachedConversations: AIDraftConversation[] | null = null
let _cacheTimestamp = 0
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export default function InboxPage() {
  const { tenantId: contextTenantId } = useTenant()
  const TENANT_ID = GMAIL_TENANT_ID_OVERRIDE || contextTenantId || ''
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [conversationDetail, setConversationDetail] = useState<ConversationDetail | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showPendingOnly, setShowPendingOnly] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Draft state
  const [draft, setDraft] = useState<Draft | null>(null)
  const [draftContent, setDraftContent] = useState('')
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  // Email sync state
  const [isSyncingEmails, setIsSyncingEmails] = useState(false)
  const [syncResult, setSyncResult] = useState<{
    conversations: number
    messages: number
    labelFound?: boolean | null
    labeledCount?: number | null
    draftsCount?: number | null
    gmailProfileEmail?: string | null
    queryUsed?: string | null
  } | null>(null)
  const [debugResult, setDebugResult] = useState<Record<string, any> | null>(null)
  const [isDebuggingEmails, setIsDebuggingEmails] = useState(false)

  // Gmail integration state
  const [gmailStatus, setGmailStatus] = useState<{
    connected: boolean
    email_address?: string
    error?: string
  } | null>(null)
  const [checkingGmail, setCheckingGmail] = useState(false)
 
  // Email conversations state (always shown by default now)
  const [aiDraftConversations, setAIDraftConversations] = useState<AIDraftConversation[]>([])
  const [isLoadingAIDrafts, setIsLoadingAIDrafts] = useState(false)
  const [selectedAIDraft, setSelectedAIDraft] = useState<AIDraftConversation | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [totalEmails, setTotalEmails] = useState(0)

  // Date range filter state
  const [sinceDays, setSinceDays] = useState(30)
  const [customDateRange, setCustomDateRange] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // Thread details lazy loading state
  const [isLoadingThreadDetails, setIsLoadingThreadDetails] = useState(false)
  const [threadDetailsError, setThreadDetailsError] = useState<string | null>(null)

  // AI Router classification state
  const [isClassifying, setIsClassifying] = useState(false)
  const [classifyError, setClassifyError] = useState<string | null>(null)

  // Bulk classification state
  const [isClassifyingAll, setIsClassifyingAll] = useState(false)
  const [cancelClassifyAll, setCancelClassifyAll] = useState(false)
  const [classifyAllProgress, setClassifyAllProgress] = useState<{ current: number; total: number } | null>(null)
  const [classifyAllError, setClassifyAllError] = useState<string | null>(null)
  const [showClassifyConfirm, setShowClassifyConfirm] = useState(false)

  // Bulk draft generation state
  const [isGeneratingAllDrafts, setIsGeneratingAllDrafts] = useState(false)
  const [cancelGenerateAllDrafts, setCancelGenerateAllDrafts] = useState(false)
  const [generateAllProgress, setGenerateAllProgress] = useState<{ current: number; total: number } | null>(null)
  const [generateAllError, setGenerateAllError] = useState<string | null>(null)
  const [showDraftsConfirm, setShowDraftsConfirm] = useState(false)

  // AI Draft generation with full prompts
  const [isGeneratingAIResponse, setIsGeneratingAIResponse] = useState(false)
  const [aiResponseError, setAIResponseError] = useState<string | null>(null)
  const [generatedResponse, setGeneratedResponse] = useState<string | null>(null)

  // Draft versioning and editing state
  const [draftVersions, setDraftVersions] = useState<DraftVersion[]>([])
  const [currentDraftVersion, setCurrentDraftVersion] = useState<DraftVersion | null>(null)
  const [isEditingDraft, setIsEditingDraft] = useState(false)
  const [editedDraftSubject, setEditedDraftSubject] = useState('')
  const [editedDraftBody, setEditedDraftBody] = useState('')
  const [isSavingDraftVersion, setIsSavingDraftVersion] = useState(false)
  const [isSendingDraft, setIsSendingDraft] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [draftVersionError, setDraftVersionError] = useState<string | null>(null)

  // Retry state for better user feedback
  const [retryAttempt, setRetryAttempt] = useState<{ current: number; max: number } | null>(null)
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null)

  // Auto-draft configuration state
  const [autoDraftEnabled, setAutoDraftEnabled] = useState(false)
  const [isLoadingAutoDraft, setIsLoadingAutoDraft] = useState(false)
  const [autoDraftError, setAutoDraftError] = useState<string | null>(null)
  const [autoDraftBatchSize, setAutoDraftBatchSize] = useState(50) // Batch size for processing
  const [isEditingBatchSize, setIsEditingBatchSize] = useState(false)
  const [autoDraftInterval, setAutoDraftInterval] = useState(5) // Interval in minutes
  const [isEditingInterval, setIsEditingInterval] = useState(false)

  // Auto-send configuration state
  const [autoSendEnabled, setAutoSendEnabled] = useState(false)
  const [autoSendLimit, setAutoSendLimit] = useState(0)
  const [isEditingSendLimit, setIsEditingSendLimit] = useState(false)

  // Auto-draft progress state
  const [autoDraftProgress, setAutoDraftProgress] = useState<{
    status: string
    total_threads: number
    classified: number
    drafts_generated: number
    drafts_sent: number
    current_thread: number
    total_to_classify: number
    total_to_draft: number
    total_to_send: number
    message: string
  } | null>(null)

  // AI Draft editing state
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null)
  const [editedDraftContent, setEditedDraftContent] = useState<{ subject: string; body: string }>({ subject: '', body: '' })
  const [isSavingAIDraft, setIsSavingAIDraft] = useState(false)
  const [aiDraftSaveError, setAIDraftSaveError] = useState<string | null>(null)

  // AI Draft sending state
  const [sendingDraftId, setSendingDraftId] = useState<string | null>(null)
  const [sendDraftError, setSendDraftError] = useState<string | null>(null)
  const [sendDraftSuccess, setSendDraftSuccess] = useState<string | null>(null)

  useEffect(() => {
    // Check for cached data first (stale-while-revalidate)
    const hasFreshCache = _cachedConversations && (Date.now() - _cacheTimestamp < CACHE_TTL)

    if (hasFreshCache) {
      // Show cached data immediately - no loading spinner
      setAIDraftConversations(_cachedConversations!)
      setTotalEmails(_cachedConversations!.length)
      // Revalidate silently in background
      loadAIDraftConversations({ showLoading: false })
    } else {
      // No cache or stale - full load with spinner
      loadAIDraftConversations({ showLoading: true })
    }

    checkGmailStatus()
    loadAutoDraftConfig()
  }, [sinceDays]) // Only reload when date range changes, not pagination

  // Poll for auto-draft progress when enabled
  useEffect(() => {
    if (!autoDraftEnabled) {
      setAutoDraftProgress(null)
      return
    }

    let pollInterval: number

    const pollProgress = async () => {
      try {
        const token = await getAuthToken()
        const response = await fetch(`${API_BASE}/v1/router/auto-draft-progress`, {
          headers: {
            'X-Tenant-Id': TENANT_ID,
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          // Only update if status is not idle
          if (data.status !== 'idle') {
            setAutoDraftProgress(data)
          } else {
            setAutoDraftProgress(null)
          }

          // Stop polling if completed or error
          if (data.status === 'completed' || data.status === 'error') {
            if (pollInterval) clearInterval(pollInterval)
            // Refresh conversations after completion
            if (data.status === 'completed') {
              setTimeout(() => {
                loadAIDraftConversations()
                setAutoDraftProgress(null)
              }, 2000)
            }
          }
        }
      } catch (err) {
        console.error('Error polling auto-draft progress:', err)
      }
    }

    // Initial poll
    pollProgress()

    // Poll every 2 seconds
    pollInterval = setInterval(pollProgress, 2000)

    return () => {
      if (pollInterval) clearInterval(pollInterval)
    }
  }, [autoDraftEnabled])

  const checkGmailStatus = async () => {
    setCheckingGmail(true)
    try {
      const response = await fetch(`${API_BASE}/v1/conversations/check-gmail`, {
        headers: {
          'X-Tenant-Id': TENANT_ID,
        }
      })

      if (response.ok) {
        const data = await response.json()
        setGmailStatus(data)
      }
    } catch (err) {
      console.error('Error checking Gmail status:', err)
      setGmailStatus({ connected: false, error: 'Failed to check Gmail status' })
    } finally {
      setCheckingGmail(false)
    }
  }

  const loadAutoDraftConfig = async () => {
    try {
      const token = await getAuthToken()
      const response = await fetch(`${API_BASE}/v1/router/auto-draft-config`, {
        headers: {
          'X-Tenant-Id': TENANT_ID,
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAutoDraftEnabled(data.auto_draft_enabled || false)
        setAutoDraftBatchSize(data.auto_draft_max_per_run || 50)
        setAutoDraftInterval(data.auto_draft_interval_minutes || 5)
        setAutoSendEnabled(data.auto_send_enabled || false)
        setAutoSendLimit(data.auto_send_limit || 0)
      }
    } catch (err) {
      console.error('Error loading auto-draft config:', err)
    }
  }

  const loadAutoDraftStatus = async () => {
    try {
      const token = await getAuthToken()
      const response = await fetch(`${API_BASE}/v1/router/auto-draft-status`, {
        headers: {
          'X-Tenant-Id': TENANT_ID,
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAutoDraftProgress(data)
      }
    } catch (err) {
      console.error('Error loading auto-draft status:', err)
    }
  }

  // Poll auto-draft status every 30 seconds when enabled
  useEffect(() => {
    if (autoDraftEnabled) {
      loadAutoDraftStatus()
      const interval = setInterval(loadAutoDraftStatus, 30000)
      return () => clearInterval(interval)
    }
  }, [autoDraftEnabled])

  // Close modal with ESC key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedAIDraft) {
        setSelectedAIDraft(null)
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [selectedAIDraft])

  const toggleAutoDraft = async () => {
    setIsLoadingAutoDraft(true)
    setAutoDraftError(null)

    try {
      const token = await getAuthToken()
      const newValue = !autoDraftEnabled

      const response = await fetch(`${API_BASE}/v1/router/auto-draft-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': TENANT_ID,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          auto_draft_enabled: newValue,
          auto_draft_since: newValue ? new Date().toISOString() : null
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to update auto-draft configuration')
      }

      const data = await response.json()
      setAutoDraftEnabled(data.config.auto_draft_enabled)

      // Show success message
      console.log(`Auto-draft ${newValue ? 'enabled' : 'disabled'} successfully`)

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update auto-draft configuration'
      setAutoDraftError(errorMsg)
      console.error('Error toggling auto-draft:', err)
      setTimeout(() => setAutoDraftError(null), 5000)
    } finally {
      setIsLoadingAutoDraft(false)
    }
  }

  const updateBatchSize = async (newSize: number) => {
    if (newSize < 1 || newSize > 200) {
      alert('Batch size must be between 1 and 200')
      return
    }

    setIsLoadingAutoDraft(true)
    try {
      const token = await getAuthToken()

      const response = await fetch(`${API_BASE}/v1/router/auto-draft-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': TENANT_ID,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          auto_draft_max_per_run: newSize
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update batch size')
      }

      const data = await response.json()
      setAutoDraftBatchSize(data.config.auto_draft_max_per_run)
      setIsEditingBatchSize(false)
      console.log(`Batch size updated to ${newSize}`)

    } catch (err) {
      console.error('Error updating batch size:', err)
      alert('Failed to update batch size')
    } finally {
      setIsLoadingAutoDraft(false)
    }
  }

  const updateInterval = async (newInterval: number) => {
    if (newInterval < 1 || newInterval > 60) {
      alert('Interval must be between 1 and 60 minutes')
      return
    }

    setIsLoadingAutoDraft(true)
    try {
      const token = await getAuthToken()

      const response = await fetch(`${API_BASE}/v1/router/auto-draft-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': TENANT_ID,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          auto_draft_interval_minutes: newInterval
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update interval')
      }

      const data = await response.json()
      setAutoDraftInterval(data.config.auto_draft_interval_minutes)
      setIsEditingInterval(false)
      console.log(`Interval updated to ${newInterval} minutes`)

    } catch (err) {
      console.error('Error updating interval:', err)
      alert('Failed to update interval')
    } finally {
      setIsLoadingAutoDraft(false)
    }
  }

  const toggleAutoSend = async () => {
    const newValue = !autoSendEnabled

    // Confirmation dialog when ENABLING auto-send
    if (newValue) {
      const confirmed = window.confirm(
        'WARNING: Enabling auto-send will automatically send AI-generated draft responses to customers without human review.\n\n' +
        'This means emails will be sent on behalf of your company without manual approval.\n\n' +
        'Are you sure you want to enable auto-send?'
      )
      if (!confirmed) return
    }

    setIsLoadingAutoDraft(true)
    try {
      const token = await getAuthToken()

      const response = await fetch(`${API_BASE}/v1/router/auto-draft-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': TENANT_ID,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          auto_send_enabled: newValue
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update auto-send configuration')
      }

      const data = await response.json()
      setAutoSendEnabled(data.config.auto_send_enabled)
      console.log(`Auto-send ${newValue ? 'enabled' : 'disabled'} successfully`)

    } catch (err) {
      console.error('Error toggling auto-send:', err)
      alert('Failed to update auto-send configuration')
    } finally {
      setIsLoadingAutoDraft(false)
    }
  }

  const updateSendLimit = async (newLimit: number) => {
    if (newLimit < -1 || newLimit > 200) {
      alert('Send limit must be between -1 (all) and 200')
      return
    }

    setIsLoadingAutoDraft(true)
    try {
      const token = await getAuthToken()

      const response = await fetch(`${API_BASE}/v1/router/auto-draft-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': TENANT_ID,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          auto_send_limit: newLimit
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update send limit')
      }

      const data = await response.json()
      setAutoSendLimit(data.config.auto_send_limit)
      setIsEditingSendLimit(false)
      console.log(`Send limit updated to ${newLimit}`)

    } catch (err) {
      console.error('Error updating send limit:', err)
      alert('Failed to update send limit')
    } finally {
      setIsLoadingAutoDraft(false)
    }
  }

  const loadConversations = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const endpoint = showPendingOnly
        ? `${API_BASE}/v1/conversations/pending`
        : `${API_BASE}/v1/conversations`

      const params = new URLSearchParams({ limit: '200' })
      if (statusFilter !== 'all' && !showPendingOnly) {
        params.append('status', statusFilter)
      }

      const response = await fetch(`${endpoint}?${params}`, {
        headers: {
          'X-Tenant-Id': TENANT_ID,
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load conversations')
      }

      const data = await response.json()

      // Map API response to our interface
      const mapped = (data.conversations || []).map((conv: any) => ({
        id: conv.session_id || conv.id,
        session_id: conv.session_id || conv.id,
        customer_email: conv.customer_email || conv.customerEmail || 'Unknown',
        customer_name: conv.customer_name || conv.customer_email?.split('@')[0] || 'Customer',
        last_message: conv.last_message || conv.lastMessage || '',
        category: conv.category || 'General',
        created_at: conv.created_at || conv.timestamp || new Date().toISOString(),
        updated_at: conv.updated_at,
        status: conv.status || 'active',
        message_count: conv.message_count || conv.messageCount || 1,
        agent_name: conv.agent_name || conv.agentName || 'Hanna',
        has_draft: conv.has_draft || false,
        needs_response: conv.needs_response || false,
      }))

      setConversations(mapped)
    } catch (err) {
      console.error('Error loading conversations:', err)
      setError('Failed to load conversations')
    } finally {
      setIsLoading(false)
    }
  }

  const loadConversationDetail = useCallback(async (sessionId: string) => {
    setIsLoadingDetail(true)
    setDraft(null)
    setDraftContent('')
    setDraftError(null)

    try {
      const response = await fetch(`${API_BASE}/v1/conversations/${sessionId}`, {
        headers: {
          'X-Tenant-Id': TENANT_ID,
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load conversation')
      }

      const data = await response.json()
      setConversationDetail({
        conversation: data.conversation,
        messages: data.messages || []
      })

      // Try to load existing draft
      await loadDraft(sessionId)

    } catch (err) {
      console.error('Error loading conversation detail:', err)
    } finally {
      setIsLoadingDetail(false)
    }
  }, [])

  const loadDraft = async (sessionId: string) => {
    try {
      const response = await fetch(`${API_BASE}/v1/conversations/${sessionId}/draft`, {
        headers: {
          'X-Tenant-Id': TENANT_ID,
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.draft) {
          // If draft is currently generating, start polling
          if (data.draft.status === 'generating') {
            setIsGeneratingDraft(true)
            await pollForDraft(sessionId)
          } else {
            setDraft(data.draft)
            setDraftContent(data.draft.content)
          }
        }
      }
    } catch (err) {
      // No draft exists, that's fine
    }
  }

  const generateDraft = async (regenerate: boolean = false) => {
    if (!selectedConversation) return

    setIsGeneratingDraft(true)
    setDraftError(null)

    try {
      const response = await fetch(
        `${API_BASE}/v1/conversations/${selectedConversation.session_id}/draft`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': TENANT_ID,
          },
          body: JSON.stringify({ regenerate })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to generate draft')
      }

      const data = await response.json()
      // If draft is already ready (from cache), display it immediately
      if (data.draft?.status === 'ready' || data.from_cache) {
        setDraft(data.draft)
        setDraftContent(data.draft.content)
        setIsEditing(false)
        setIsGeneratingDraft(false)
        return
      }

      // If status is 'generating', start polling
      if (data.status === 'generating' || data.draft?.status === 'generating') {
        await pollForDraft(selectedConversation.session_id)
      } else {
        // Fallback: display whatever we got
        setDraft(data.draft)
        setDraftContent(data.draft?.content || '')
        setIsEditing(false)
        setIsGeneratingDraft(false)
      }
      } catch (err) {
        console.error('Error generating draft:', err)
        setDraftError(err instanceof Error ? err.message : 'Failed to generate draft')
        setIsGeneratingDraft(false)
      }
      }

      const pollForDraft = async (sessionId: string, maxAttempts: number = 60) => {
        const pollInterval = 2000 // Poll every 2 seconds
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          try {
            const response = await fetch(`${API_BASE}/v1/conversations/${sessionId}/draft`, {
              headers: {
                'X-Tenant-Id': TENANT_ID,
              }
            })
            if (response.ok) {
              const data = await response.json()
              if (data.draft?.status === 'ready' || (data.draft?.content && data.draft.content.trim() !== '')) {
                // Draft is ready!
                setDraft(data.draft)
                setDraftContent(data.draft.content)
                setIsEditing(false)
                setIsGeneratingDraft(false)
                return
              }
              // Still generating, continue polling
              if (data.draft?.status === 'generating') {
                await new Promise(resolve => setTimeout(resolve, pollInterval))
                continue
              }
            }
            // Wait before next attempt
            await new Promise(resolve => setTimeout(resolve, pollInterval))
          } catch (err) {
            console.error('Error polling for draft:', err)
            // Continue polling on error
            await new Promise(resolve => setTimeout(resolve, pollInterval))
          }
        }
        // Timeout after max attempts
        setDraftError('Draft generation timed out. Please try again.')
        setIsGeneratingDraft(false)
      }

  const saveDraft = async () => {
    if (!selectedConversation || !draftContent.trim()) return

    setIsSavingDraft(true)

    try {
      const response = await fetch(
        `${API_BASE}/v1/conversations/${selectedConversation.session_id}/draft`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': TENANT_ID,
          },
          body: JSON.stringify({
            content: draftContent,
            status: 'edited'
          })
        }
      )

      if (!response.ok) {
        throw new Error('Failed to save draft')
      }

      const data = await response.json()
      setDraft(data.draft)
      setIsEditing(false)

    } catch (err) {
      console.error('Error saving draft:', err)
      setDraftError('Failed to save draft')
    } finally {
      setIsSavingDraft(false)
    }
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(draftContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const syncEmailsFromGmail = async () => {
    setIsSyncingEmails(true)
    setSyncResult(null)
    setError(null)

    try {
      const response = await fetch(`${API_BASE}/v1/conversations/sync-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': TENANT_ID,
        },
        body: JSON.stringify({
          label_name: 'AI-Draft-Created',
          include_drafts: true,
          since_days: 7,
          max_emails: 50,
          ...(GMAIL_SYNC_QUERY ? { query: GMAIL_SYNC_QUERY } : {})
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to sync emails')
      }

      const data = await response.json()
      setSyncResult({
        conversations: data.synced_conversations || 0,
        messages: data.synced_messages || 0,
        labelFound: data.details?.label_found ?? null,
        labeledCount: data.details?.labeled_emails_count ?? null,
        draftsCount: data.details?.drafts_count ?? null,
        gmailProfileEmail: data.details?.gmail_profile_email ?? null,
        queryUsed: data.details?.query_used ?? null
      })

      // Reload conversations to show newly synced emails
      await loadConversations()

      // Auto-hide success message after 5 seconds
      setTimeout(() => setSyncResult(null), 5000)

    } catch (err) {
      console.error('Error syncing emails:', err)
      setError(err instanceof Error ? err.message : 'Failed to sync emails from Gmail')
    } finally {
      setIsSyncingEmails(false)
    }
  }

  const debugEmailsFromGmail = async () => {
    setIsDebuggingEmails(true)
    setDebugResult(null)
    setError(null)

    try {
      const response = await fetch(`${API_BASE}/v1/conversations/sync-emails-debug`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': TENANT_ID,
        },
        body: JSON.stringify({
          label_name: 'AI-Draft-Created',
          include_drafts: true,
          since_days: 7,
          max_emails: 50,
          ...(GMAIL_SYNC_QUERY ? { query: GMAIL_SYNC_QUERY } : {})
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to debug Gmail sync')
      }

      const data = await response.json()
      setDebugResult(data)
    } catch (err) {
      console.error('Error debugging Gmail sync:', err)
      setError(err instanceof Error ? err.message : 'Failed to debug Gmail sync')
    } finally {
      setIsDebuggingEmails(false)
    }
  }
 
  const loadAIDraftConversations = async (options?: { showLoading?: boolean }) => {
    const showLoading = options?.showLoading ?? true

    if (showLoading) {
      setIsLoadingAIDrafts(true)
      setLoadingStartTime(Date.now())
    }
    setError(null)
    setRetryAttempt(null)

    try {
      const token = await getAuthToken()
      if (!token) {
        throw new Error('Not authenticated')
      }

      // Calculate max_emails based on current page and items per page
      // Optimized: Dynamically calculate based on pagination to fetch only what's needed
      // This reduces Gmail API batching and improves load times significantly
      // Fetch 2x the items per page to allow for browsing without re-fetching, with a min of 50
      const maxEmailsToFetch = Math.max(50, itemsPerPage * 2)

      const params = new URLSearchParams({
        since_days: String(sinceDays),
        max_emails: String(maxEmailsToFetch),
        all_emails: 'true',  // Fetch ALL inbox emails by default
        lite: 'true',  // Optimized: Use lite mode for faster initial load (drafts/classifications loaded on-demand)
        ...(GMAIL_SYNC_QUERY ? { query: GMAIL_SYNC_QUERY } : {})
      })

      const data = await fetchWithRetry<{
        conversations: AIDraftConversation[]
        total_threads?: number
        total_emails?: number
      }>(
        `${API_BASE}/v1/conversations/ai-drafts?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Id': TENANT_ID,
          },
          retries: MAX_RETRIES,
          retryDelay: INITIAL_RETRY_DELAY,
        },
        (attempt, max) => {
          setRetryAttempt({ current: attempt, max })
        }
      )

      const allConversations = data.conversations || []

      // Update module-level cache
      _cachedConversations = allConversations
      _cacheTimestamp = Date.now()

      setTotalEmails(allConversations.length)
      setAIDraftConversations(allConversations)
      setRetryAttempt(null)

      // Reset to first page if current page is out of bounds
      const totalPages = Math.ceil(allConversations.length / itemsPerPage)
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(1)
      }
    } catch (err) {
      console.error('Error loading AI draft conversations:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to load AI draft conversations'

      // Provide more helpful error messages
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('AbortError') || errorMessage.includes('timeout')) {
        setError('Connection timed out. The server may be busy processing your request. Click "Retry" to try again.')
      } else if (errorMessage.includes('Not authenticated')) {
        setError('Your session has expired. Please refresh the page to log in again.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setIsLoadingAIDrafts(false)
      setLoadingStartTime(null)
    }
  }

  // Load thread details (classification + drafts) on demand when clicking a thread
  const loadThreadDetails = async (thread_id: string): Promise<{
    classification: { assigned_agent_id: string; assigned_agent_name: string; classified_at: string; response_mode?: string; draft_response_enabled?: boolean } | null;
    drafts: AIDraftItem[];
  } | null> => {
    try {
      const token = await getAuthToken()
      if (!token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/v1/conversations/thread-details/${thread_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': TENANT_ID,
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to load thread details')
      }

      const data = await response.json()

      // Debug: Log draft IDs from API response
      if (data.drafts && data.drafts.length > 0) {
        console.log('[DEBUG] Drafts loaded from API:', data.drafts.length)
        data.drafts.forEach((draft: AIDraftItem, idx: number) => {
          console.log(`[DEBUG] Draft ${idx} ID:`, draft.draft_id)
          console.log(`[DEBUG] Draft ${idx} ID characters:`, draft.draft_id?.split('').map((c, i) => `${i}: '${c}' (${c.charCodeAt(0)})`).join(', '))
        })
      }

      return {
        classification: data.classification,
        drafts: data.drafts || []
      }
    } catch (err) {
      console.error('Error loading thread details:', err)
      throw err
    }
  }

  // Handle selecting a thread - load details lazily
  const handleSelectThread = async (conv: AIDraftConversation) => {
    // Set the conversation immediately for better UX (show the modal)
    setSelectedAIDraft(conv)
    setIsLoadingThreadDetails(true)
    setThreadDetailsError(null)

    try {
      const details = await loadThreadDetails(conv.thread_id)
      if (details) {
        // Update the selected draft with loaded details
        setSelectedAIDraft(prev => {
          if (!prev || prev.thread_id !== conv.thread_id) return prev
          return {
            ...prev,
            assigned_agent_id: details.classification?.assigned_agent_id || null,
            assigned_agent_name: details.classification?.assigned_agent_name || null,
            response_mode: details.classification?.response_mode || null,
            draft_response_enabled: details.classification?.draft_response_enabled ?? null,
            drafts: details.drafts,
            draft_count: details.drafts.length  // Update draft_count with actual count
          }
        })

        // Also update in the list for consistency
        setAIDraftConversations(prevList =>
          prevList.map(c =>
            c.thread_id === conv.thread_id
              ? {
                  ...c,
                  assigned_agent_id: details.classification?.assigned_agent_id || null,
                  assigned_agent_name: details.classification?.assigned_agent_name || null,
                  response_mode: details.classification?.response_mode || null,
                  draft_response_enabled: details.classification?.draft_response_enabled ?? null,
                  drafts: details.drafts,
                  draft_count: details.drafts.length  // Update draft_count with actual count
                }
              : c
          )
        )
      }
    } catch (err) {
      setThreadDetailsError(err instanceof Error ? err.message : 'Failed to load thread details')
    } finally {
      setIsLoadingThreadDetails(false)
    }
  }

  const classifyEmailWithRouter = async (thread_id: string) => {
    setIsClassifying(true)
    setClassifyError(null)

    try {
      const token = await getAuthToken()
      if (!token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/v1/conversations/classify-thread`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': TENANT_ID,
        },
        body: JSON.stringify({ thread_id })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to classify email')
      }

      const data = await response.json()

      // Update the selected draft with classification
      if (selectedAIDraft && selectedAIDraft.thread_id === thread_id) {
        setSelectedAIDraft({
          ...selectedAIDraft,
          assigned_agent_id: data.assigned_agent_id,
          assigned_agent_name: data.assigned_agent_name
        })
      }

      // Update in list
      setAIDraftConversations(prev =>
        prev.map(conv =>
          conv.thread_id === thread_id
            ? { ...conv, assigned_agent_id: data.assigned_agent_id, assigned_agent_name: data.assigned_agent_name }
            : conv
        )
      )

      return data
    } catch (err) {
      console.error('Error classifying email:', err)
      setClassifyError(err instanceof Error ? err.message : 'Failed to classify email')
      throw err
    } finally {
      setIsClassifying(false)
    }
  }

  const classifyAllUnclassified = async () => {
    // Check if there are unclassified conversations
    const unclassifiedConversations = aiDraftConversations.filter(
      conv => !conv.assigned_agent_id && !conv.assigned_agent_name
    )

    // Determine which threads to classify:
    // - If there are unclassified, classify only those
    // - If all are classified, reclassify all threads
    const threadsToClassify = unclassifiedConversations.length > 0
      ? unclassifiedConversations
      : aiDraftConversations

    if (threadsToClassify.length === 0) {
      setClassifyAllError('No conversations found!')
      setTimeout(() => setClassifyAllError(null), 3000)
      return
    }

    setIsClassifyingAll(true)
    setCancelClassifyAll(false)
    setClassifyAllError(null)
    setClassifyAllProgress({ current: 0, total: threadsToClassify.length })

    let successCount = 0
    let failedCount = 0
    let cancelled = false

    try {
      // Classify in batches to avoid overwhelming the API
      // We'll do them sequentially to be safe
      for (let i = 0; i < threadsToClassify.length; i++) {
        // Check if user cancelled
        if (cancelClassifyAll) {
          cancelled = true
          setClassifyAllError(`Cancelled: Classified ${successCount}/${threadsToClassify.length} conversations`)
          break
        }

        const conv = threadsToClassify[i]

        try {
          await classifyEmailWithRouter(conv.thread_id)
          successCount++
        } catch (err) {
          console.error(`Failed to classify thread ${conv.thread_id}:`, err)
          failedCount++
        }

        setClassifyAllProgress({ current: i + 1, total: threadsToClassify.length })

        // Small delay between requests to avoid rate limiting
        if (i < threadsToClassify.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }

      // Show success message (only if not cancelled)
      if (!cancelled) {
        if (failedCount === 0) {
          setClassifyAllError(null)
        } else {
          setClassifyAllError(`Classified ${successCount} conversations. Failed: ${failedCount}`)
        }
      }

      // Reload conversations to show updated classifications
      await loadAIDraftConversations()

    } catch (err) {
      console.error('Error during bulk classification:', err)
      setClassifyAllError(err instanceof Error ? err.message : 'Failed to classify conversations')
    } finally {
      setIsClassifyingAll(false)
      setCancelClassifyAll(false)
      // Clear progress after a delay
      setTimeout(() => setClassifyAllProgress(null), 3000)
    }
  }

  const generateAIResponse = async (thread_id: string, agent_id: string) => {
    setIsGeneratingAIResponse(true)
    setAIResponseError(null)
    setGeneratedResponse(null)

    try {
      const token = await getAuthToken()
      if (!token) {
        throw new Error('Not authenticated')
      }

      // Step 1: Kick off async generation
      const response = await fetch(`${API_BASE}/v1/conversations/generate-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': TENANT_ID,
        },
        body: JSON.stringify({ thread_id, agent_id })
      })

      // Handle validation errors (4xx) immediately
      if (response.status >= 400 && response.status < 500) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to generate response')
      }

      const data = await response.json()

      // Handle async processing response
      if (data.status === 'processing') {
        setGeneratedResponse('Generating AI response... Draft will appear shortly.')
        // Poll for completion: reload both the sidebar list AND the open thread details
        const refreshAll = async () => {
          await loadAIDraftConversations()
          // Also refresh the currently open thread to show new drafts
          if (thread_id) {
            try {
              const details = await loadThreadDetails(thread_id)
              if (details) {
                setSelectedAIDraft(prev => {
                  if (!prev || prev.thread_id !== thread_id) return prev
                  return {
                    ...prev,
                    drafts: details.drafts,
                    draft_count: details.drafts.length
                  }
                })
              }
            } catch (err) {
              console.error('Error refreshing thread details:', err)
            }
          }
        }
        // Keep isGeneratingAIResponse=true until polling is done
        setTimeout(() => refreshAll(), 8000)
        setTimeout(() => refreshAll(), 18000)
        setTimeout(() => {
          refreshAll()
          setIsGeneratingAIResponse(false)
          setGeneratedResponse(null)
        }, 30000)
        return data
      }

      setGeneratedResponse(data.response)

      // Reload AI draft conversations to show the new draft
      loadAIDraftConversations()
      setIsGeneratingAIResponse(false)
      return data

    } catch (err) {
      console.error('Error generating AI response:', err)
      setAIResponseError(err instanceof Error ? err.message : 'Failed to generate response')
      setIsGeneratingAIResponse(false)
      throw err
    }
  }

  const generateAllDrafts = async () => {
    // Filter conversations that ARE classified and allow auto-drafting
    // Skip agents with response_mode="manual" or draft_response_enabled=false
    const classifiedConversations = aiDraftConversations.filter(
      conv => conv.assigned_agent_id && conv.assigned_agent_name
        && conv.response_mode !== 'manual'
        && conv.draft_response_enabled !== false
    )

    if (classifiedConversations.length === 0) {
      setGenerateAllError('No classified conversations found!')
      setTimeout(() => setGenerateAllError(null), 3000)
      return
    }

    setIsGeneratingAllDrafts(true)
    setCancelGenerateAllDrafts(false)
    setGenerateAllError(null)
    setGenerateAllProgress({ current: 0, total: classifiedConversations.length })

    let successCount = 0
    let failedCount = 0
    let cancelled = false

    try {
      for (let i = 0; i < classifiedConversations.length; i++) {
        // Check if user cancelled
        if (cancelGenerateAllDrafts) {
          cancelled = true
          setGenerateAllError(`Cancelled: Generated ${successCount}/${classifiedConversations.length} drafts`)
          break
        }

        const conv = classifiedConversations[i]

        try {
          await generateAIResponse(conv.thread_id, conv.assigned_agent_id!)
          successCount++
        } catch (err) {
          console.error(`Failed to generate draft for thread ${conv.thread_id}:`, err)
          failedCount++
        }

        setGenerateAllProgress({ current: i + 1, total: classifiedConversations.length })

        // Small delay between requests to avoid rate limiting
        if (i < classifiedConversations.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000)) // 1s delay for draft generation
        }
      }

      // Show success message (only if not cancelled)
      if (!cancelled) {
        if (failedCount === 0) {
          setGenerateAllError(null)
        } else {
          setGenerateAllError(`Generated ${successCount} drafts. Failed: ${failedCount}`)
        }
      }

      // Reload conversations to show new drafts
      await loadAIDraftConversations()

    } catch (err) {
      console.error('Error during bulk draft generation:', err)
      setGenerateAllError(err instanceof Error ? err.message : 'Failed to generate drafts')
    } finally {
      setIsGeneratingAllDrafts(false)
      setCancelGenerateAllDrafts(false)
      // Clear progress after a delay
      setTimeout(() => setGenerateAllProgress(null), 3000)
    }
  }

  const saveEditedAIDraft = async (draftId: string, threadId: string) => {
    console.log('[DEBUG] Saving draft with ID:', draftId)
    console.log('[DEBUG] Using thread ID for API:', threadId)

    setIsSavingAIDraft(true)
    setAIDraftSaveError(null)

    try {
      const token = await getAuthToken()
      if (!token) {
        throw new Error('Not authenticated')
      }

      // IMPORTANT: The API uses thread_id in the URL, NOT draft_id
      // The route is: PUT /v1/drafts/{thread_id}
      const url = `${API_BASE}/v1/drafts/${threadId}`
      console.log('[DEBUG] PUT URL:', url)

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': TENANT_ID,
        },
        body: JSON.stringify({
          subject: editedDraftContent.subject,
          body: editedDraftContent.body,
          to: selectedAIDraft?.customer_email || '',
          edited_by: 'user@example.com',
          edit_reason: 'manual_edit'
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to save draft')
      }

      const data = await response.json()

      // Update the draft in the selected thread
      if (selectedAIDraft && selectedAIDraft.thread_id === threadId) {
        setSelectedAIDraft({
          ...selectedAIDraft,
          drafts: selectedAIDraft.drafts.map(d =>
            d.draft_id === draftId
              ? { ...d, subject: editedDraftContent.subject, body: editedDraftContent.body }
              : d
          )
        })
      }

      // Update in the list
      setAIDraftConversations(prev =>
        prev.map(conv =>
          conv.thread_id === threadId
            ? {
                ...conv,
                drafts: conv.drafts.map(d =>
                  d.draft_id === draftId
                    ? { ...d, subject: editedDraftContent.subject, body: editedDraftContent.body }
                    : d
                )
              }
            : conv
        )
      )

      // Exit edit mode
      setEditingDraftId(null)
      setEditedDraftContent({ subject: '', body: '' })

      return data
    } catch (err) {
      console.error('Error saving AI draft:', err)
      setAIDraftSaveError(err instanceof Error ? err.message : 'Failed to save draft')
      throw err
    } finally {
      setIsSavingAIDraft(false)
    }
  }

  const sendDraftResponse = async (draftId: string, threadId: string, versionNumber: number = 1) => {
    console.log('[DEBUG] Sending draft with ID:', draftId, 'thread:', threadId, 'version:', versionNumber)

    setSendingDraftId(draftId)
    setSendDraftError(null)
    setSendDraftSuccess(null)

    try {
      const token = await getAuthToken()
      if (!token) {
        throw new Error('Not authenticated')
      }

      // The API route is: POST /v1/drafts/{thread_id}?action=send
      const url = `${API_BASE}/v1/drafts/${threadId}?action=send`
      console.log('[DEBUG] POST URL:', url)

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': TENANT_ID,
        },
        body: JSON.stringify({
          draft_id: draftId,
          version_number: versionNumber
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to send draft')
      }

      const data = await response.json()
      console.log('[DEBUG] Send response:', data)

      // Show success message
      setSendDraftSuccess('Draft sent successfully!')

      // Reload conversations to reflect the sent status
      setTimeout(() => {
        loadAIDraftConversations()
        setSendDraftSuccess(null)
        // Close the modal or update the UI
        setSelectedAIDraft(null)
      }, 2000)

      return data
    } catch (err) {
      console.error('Error sending draft:', err)
      setSendDraftError(err instanceof Error ? err.message : 'Failed to send draft')
      throw err
    } finally {
      setSendingDraftId(null)
    }
  }

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation)
    loadConversationDetail(conversation.session_id)
  }

  const closeModal = () => {
    setSelectedConversation(null)
    setConversationDetail(null)
    setDraft(null)
    setDraftContent('')
    setDraftError(null)
    setIsEditing(false)
  }

  const filteredConversations = conversations.filter(conv => {
    const searchLower = searchQuery.toLowerCase()
    const matchesSearch =
      (conv.customer_email?.toLowerCase() || '').includes(searchLower) ||
      (conv.customer_name?.toLowerCase() || '').includes(searchLower) ||
      (conv.last_message?.toLowerCase() || '').includes(searchLower)
    return matchesSearch
  })

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      resolved: 'bg-gray-100 text-gray-700',
      escalated: 'bg-red-100 text-red-700',
    }
    return colors[status] || 'bg-gray-100 text-gray-700'
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  const stats = {
    active: conversations.filter(c => c.status === 'active').length,
    resolved: conversations.filter(c => c.status === 'resolved').length,
    escalated: conversations.filter(c => c.status === 'escalated').length,
    pending: conversations.filter(c => c.needs_response).length,
  }

  const emailMessages = conversationDetail?.messages.filter(
    msg => msg.metadata?.source === 'email'
  ) || []
  const emailMessagesNewestFirst = [...emailMessages].reverse()
  const latestInboundEmail = emailMessagesNewestFirst.find(msg => {
    const direction = msg.metadata?.direction
    return direction !== 'sent' && direction !== 'draft'
  })
  const latestDraftEmail = emailMessagesNewestFirst.find(msg => msg.metadata?.direction === 'draft')

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Conversations</h1>
          <p className="text-gray-500 mt-1">View and manage customer conversations</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            {gmailStatus && (
              <span className={`text-xs px-2 py-1 rounded-full ${
                gmailStatus.connected
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {gmailStatus.connected
                  ? `Gmail: ${gmailStatus.email_address || 'Connected'}`
                  : 'Gmail: Not connected'}
              </span>
            )}
            <button
              onClick={syncEmailsFromGmail}
              disabled={isSyncingEmails || !gmailStatus?.connected}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                gmailStatus?.connected
                  ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              title={gmailStatus?.connected
                ? 'Sync emails from Gmail with AI-Draft-Created label'
                : 'Configure Gmail in Integrations first'
              }
            >
              <Mail className={`w-4 h-4 ${isSyncingEmails ? 'animate-pulse' : ''}`} />
              <span>{isSyncingEmails ? 'Syncing...' : 'Sync Gmail'}</span>
            </button>
            <button
              onClick={debugEmailsFromGmail}
              disabled={isDebuggingEmails || !gmailStatus?.connected}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                gmailStatus?.connected
                  ? 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              title={gmailStatus?.connected
                ? 'Debug Gmail sync and list threads/drafts'
                : 'Configure Gmail in Integrations first'
              }
            >
              <FileText className={`w-4 h-4 ${isDebuggingEmails ? 'animate-pulse' : ''}`} />
              <span>{isDebuggingEmails ? 'Debugging...' : 'Debug Gmail'}</span>
            </button>
          </div>
          <button
            onClick={loadConversations}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Sync Result Notification */}
      {syncResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-700">
            <Check className="w-5 h-5" />
            <span>
              Synced {syncResult.conversations} new conversations and {syncResult.messages} messages from Gmail
              {syncResult.labeledCount !== null && syncResult.draftsCount !== null && (
                <span className="ml-2 text-green-600">
                  (Labeled emails: {syncResult.labeledCount}, drafts: {syncResult.draftsCount})
                </span>
              )}
              {syncResult.labelFound === false && (
                <span className="ml-2 text-yellow-700">
                  Label AI-Draft-Created not found.
                </span>
              )}
              {syncResult.gmailProfileEmail && (
                <span className="ml-2 text-green-700">
                  Gmail account: {syncResult.gmailProfileEmail}
                </span>
              )}
              {syncResult.queryUsed && (
                <span className="ml-2 text-green-700">
                  Query: {syncResult.queryUsed}
                </span>
              )}
            </span>
          </div>
          <button onClick={() => setSyncResult(null)} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {debugResult && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">Gmail Debug Result</p>
            <button
              onClick={() => setDebugResult(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <pre className="mt-3 max-h-60 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-700">
            {JSON.stringify(debugResult, null, 2)}
          </pre>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            />
          </div>
          <div className="flex items-center space-x-2">
            {/* Gmail status indicator */}
            {gmailStatus?.connected ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Gmail Connected
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                Gmail Not Connected
              </div>
            )}
          </div>
        </div>

        {/* Date Range and Items Per Page */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            <label className="text-sm font-medium text-gray-700">Last:</label>
            <select
              value={sinceDays}
              onChange={(e) => {
                setSinceDays(Number(e.target.value))
                setCurrentPage(1) // Reset to first page
              }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>6 months</option>
              <option value={365}>1 year</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
                setCurrentPage(1) // Reset to first page
              }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>

          <div className="flex-1"></div>

          <div className="text-sm text-gray-600">
            Total: {totalEmails} emails
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm text-green-600 font-medium">Active</p>
          <p className="text-2xl font-bold text-green-700">{stats.active}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 font-medium">Resolved</p>
          <p className="text-2xl font-bold text-gray-700">{stats.resolved}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-sm text-red-600 font-medium">Escalated</p>
          <p className="text-2xl font-bold text-red-700">{stats.escalated}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200">
          <p className="text-sm text-yellow-600 font-medium">Needs Response</p>
          <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
        </div>
      </div>

      {/* Error message with retry button */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 font-medium">Error loading conversations</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setError(null)
                loadAIDraftConversations()
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Email Conversations List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Email Conversations</h3>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-700">
                {aiDraftConversations.length} threads
              </span>
              {aiDraftConversations.filter(c => !c.assigned_agent_id).length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                  {aiDraftConversations.filter(c => !c.assigned_agent_id).length} unclassified
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Auto-Draft Toggle */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                <Bot className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-700 font-medium">Auto-Draft:</span>
                <button
                  onClick={toggleAutoDraft}
                  disabled={isLoadingAutoDraft}
                  className={`
                    relative inline-flex h-5 w-9 items-center rounded-full transition-colors
                    ${autoDraftEnabled ? 'bg-green-600' : 'bg-gray-300'}
                    ${isLoadingAutoDraft ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  title={autoDraftEnabled ? 'Disable automatic draft generation for incoming emails' : 'Enable automatic draft generation for incoming emails'}
                >
                  <span
                    className={`
                      inline-block h-3 w-3 transform rounded-full bg-white transition-transform
                      ${autoDraftEnabled ? 'translate-x-5' : 'translate-x-1'}
                    `}
                  />
                </button>
                <span className={`text-xs font-medium ${autoDraftEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                  {isLoadingAutoDraft ? 'Updating...' : autoDraftEnabled ? 'ON' : 'OFF'}
                </span>
              </div>

              {/* Batch Size Control */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-sm text-gray-700 font-medium">Batch:</span>
                {!isEditingBatchSize ? (
                  <>
                    <span className="text-sm font-mono text-gray-900">{autoDraftBatchSize}</span>
                    <button
                      onClick={() => setIsEditingBatchSize(true)}
                      disabled={isLoadingAutoDraft}
                      className="text-blue-600 hover:text-blue-700 text-xs font-medium disabled:opacity-50"
                      title="Click to edit batch size (max threads to process per run)"
                    >
                      Edit
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={autoDraftBatchSize}
                      onChange={(e) => setAutoDraftBatchSize(parseInt(e.target.value) || 1)}
                      className="w-16 px-2 py-0.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                    <button
                      onClick={() => updateBatchSize(autoDraftBatchSize)}
                      disabled={isLoadingAutoDraft}
                      className="text-green-600 hover:text-green-700 text-xs font-medium disabled:opacity-50"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingBatchSize(false)
                        loadAutoDraftConfig()
                      }}
                      className="text-red-600 hover:text-red-700 text-xs font-medium"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              {/* Interval Control */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-sm text-gray-700 font-medium">Every:</span>
                {!isEditingInterval ? (
                  <>
                    <span className="text-sm font-mono text-gray-900">{autoDraftInterval} min</span>
                    <button
                      onClick={() => setIsEditingInterval(true)}
                      disabled={isLoadingAutoDraft}
                      className="text-blue-600 hover:text-blue-700 text-xs font-medium disabled:opacity-50"
                      title="Click to edit processing interval (how often the worker runs, 1-60 min)"
                    >
                      Edit
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={autoDraftInterval}
                      onChange={(e) => setAutoDraftInterval(parseInt(e.target.value) || 5)}
                      className="w-16 px-2 py-0.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                    <span className="text-xs text-gray-500">min</span>
                    <button
                      onClick={() => updateInterval(autoDraftInterval)}
                      disabled={isLoadingAutoDraft}
                      className="text-green-600 hover:text-green-700 text-xs font-medium disabled:opacity-50"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingInterval(false)
                        loadAutoDraftConfig()
                      }}
                      className="text-red-600 hover:text-red-700 text-xs font-medium"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              {/* Auto-Send Toggle */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${autoSendEnabled ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                <span className={`text-sm font-medium ${autoSendEnabled ? 'text-red-700' : 'text-gray-700'}`}>Auto-Send:</span>
                <button
                  onClick={toggleAutoSend}
                  disabled={isLoadingAutoDraft || !autoDraftEnabled}
                  className={`
                    relative inline-flex h-5 w-9 items-center rounded-full transition-colors
                    ${autoSendEnabled ? 'bg-red-500' : 'bg-gray-300'}
                    ${isLoadingAutoDraft || !autoDraftEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  title={!autoDraftEnabled ? 'Enable Auto-Draft first' : autoSendEnabled ? 'Disable automatic sending of generated drafts' : 'Enable automatic sending of generated drafts'}
                >
                  <span
                    className={`
                      inline-block h-3 w-3 transform rounded-full bg-white transition-transform
                      ${autoSendEnabled ? 'translate-x-5' : 'translate-x-1'}
                    `}
                  />
                </button>
                <span className={`text-xs font-medium ${autoSendEnabled ? 'text-red-600' : 'text-gray-500'}`}>
                  {isLoadingAutoDraft ? 'Updating...' : autoSendEnabled ? 'LIVE' : 'OFF'}
                </span>
              </div>

              {/* Send Limit Control */}
              {autoSendEnabled && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-sm text-gray-700 font-medium">Send Limit:</span>
                  {!isEditingSendLimit ? (
                    <>
                      <span className="text-sm font-mono text-gray-900">
                        {autoSendLimit === -1 ? 'All' : autoSendLimit === 0 ? 'None' : autoSendLimit}
                      </span>
                      <button
                        onClick={() => setIsEditingSendLimit(true)}
                        disabled={isLoadingAutoDraft}
                        className="text-blue-600 hover:text-blue-700 text-xs font-medium disabled:opacity-50"
                        title="Click to edit send limit (-1 = all, 0 = none)"
                      >
                        Edit
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="-1"
                        max="200"
                        value={autoSendLimit}
                        onChange={(e) => setAutoSendLimit(parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-0.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <button
                        onClick={() => updateSendLimit(autoSendLimit)}
                        disabled={isLoadingAutoDraft}
                        className="text-green-600 hover:text-green-700 text-xs font-medium disabled:opacity-50"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingSendLimit(false)
                          loadAutoDraftConfig()
                        }}
                        className="text-red-600 hover:text-red-700 text-xs font-medium"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Classify All Unclassified Button */}
              {!isClassifyingAll ? (
                <button
                  onClick={() => setShowClassifyConfirm(true)}
                  disabled={aiDraftConversations.length === 0}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={
                    aiDraftConversations.filter(c => !c.assigned_agent_id).length === 0
                      ? "Reclassify all conversations with AI Router"
                      : "Classify all unclassified conversations with AI Router"
                  }
                >
                  <Zap className="w-4 h-4" />
                  Classify Unclassified
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {classifyAllProgress && (
                      <span>Classifying {classifyAllProgress.current}/{classifyAllProgress.total}</span>
                    )}
                  </div>
                  <button
                    onClick={() => setCancelClassifyAll(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
                    title="Stop bulk classification"
                  >
                    <X className="w-4 h-4" />
                    Stop
                  </button>
                </div>
              )}

              {/* Generate All Drafts Button */}
              {!isGeneratingAllDrafts ? (
                <button
                  onClick={() => setShowDraftsConfirm(true)}
                  disabled={aiDraftConversations.filter(c => c.assigned_agent_id).length === 0}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Generate AI drafts for all classified conversations"
                >
                  <Sparkles className="w-4 h-4" />
                  Generate All Drafts
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {generateAllProgress && (
                      <span>Generating {generateAllProgress.current}/{generateAllProgress.total}</span>
                    )}
                  </div>
                  <button
                    onClick={() => setCancelGenerateAllDrafts(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
                    title="Stop bulk draft generation"
                  >
                    <X className="w-4 h-4" />
                    Stop
                  </button>
                </div>
              )}

              <button
                onClick={() => loadAIDraftConversations({ showLoading: true })}
                disabled={isLoadingAIDrafts}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingAIDrafts ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Auto-Draft Progress Indicator */}
          {autoDraftProgress && autoDraftProgress.status !== 'idle' && (
            <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-blue-900 font-medium">
                    Auto-Draft in progress
                  </p>
                  <p className="text-blue-700 text-sm mt-1">
                    {autoDraftProgress.message}
                  </p>

                  {/* Progress bars */}
                  <div className="mt-3 space-y-2">
                    {/* Classification progress */}
                    {(autoDraftProgress.status === 'classifying' || autoDraftProgress.status === 'processing') && autoDraftProgress.total_to_classify > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-blue-600 mb-1">
                          <span>Classification</span>
                          <span>
                            {autoDraftProgress.current_thread}/{autoDraftProgress.total_to_classify}
                          </span>
                        </div>
                        <div className="w-full bg-blue-100 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${autoDraftProgress.total_to_classify > 0 ? (autoDraftProgress.current_thread / autoDraftProgress.total_to_classify) * 100 : 0}%`
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Draft generation progress */}
                    {autoDraftProgress.status === 'generating_drafts' && autoDraftProgress.total_to_draft > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-purple-600 mb-1">
                          <span>Generating Drafts</span>
                          <span>
                            {autoDraftProgress.current_thread}/{autoDraftProgress.total_to_draft}
                          </span>
                        </div>
                        <div className="w-full bg-purple-100 rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${autoDraftProgress.total_to_draft > 0 ? (autoDraftProgress.current_thread / autoDraftProgress.total_to_draft) * 100 : 0}%`
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Draft sending progress */}
                    {autoDraftProgress.status === 'sending_drafts' && autoDraftProgress.total_to_send > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-green-600 mb-1">
                          <span>Sending Drafts</span>
                          <span>
                            {autoDraftProgress.current_thread}/{autoDraftProgress.total_to_send}
                          </span>
                        </div>
                        <div className="w-full bg-green-100 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${autoDraftProgress.total_to_send > 0 ? (autoDraftProgress.current_thread / autoDraftProgress.total_to_send) * 100 : 0}%`
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status indicator */}
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    {autoDraftProgress.status === 'processing' && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                        Processing
                      </span>
                    )}
                    {autoDraftProgress.status === 'classifying' && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                        Classifying
                      </span>
                    )}
                    {autoDraftProgress.status === 'generating_drafts' && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                        Generating Drafts
                      </span>
                    )}
                    {autoDraftProgress.status === 'sending_drafts' && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        ✉️ Sending Drafts
                      </span>
                    )}
                    {autoDraftProgress.status === 'completed' && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        ✅ Completed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Classification Progress/Error */}
          {classifyAllError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{classifyAllError}</span>
              <button
                onClick={() => setClassifyAllError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {classifyAllProgress && !classifyAllError && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">
                  Classifying conversations...
                </span>
                <span className="text-sm text-blue-700">
                  {classifyAllProgress.current} / {classifyAllProgress.total}
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(classifyAllProgress.current / classifyAllProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Bulk Draft Generation Progress/Error */}
          {generateAllError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{generateAllError}</span>
              <button
                onClick={() => setGenerateAllError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {generateAllProgress && !generateAllError && (
            <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-purple-900">
                  Generating AI drafts...
                </span>
                <span className="text-sm text-purple-700">
                  {generateAllProgress.current} / {generateAllProgress.total}
                </span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(generateAllProgress.current / generateAllProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {isLoadingAIDrafts ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 text-yellow-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-500 font-medium">
              {retryAttempt
                ? `Retrying... (attempt ${retryAttempt.current} of ${retryAttempt.max})`
                : 'Loading email conversations...'}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {retryAttempt
                ? 'The server is taking longer than expected. Please wait...'
                : 'This may take a moment if you have many emails'}
            </p>
            {loadingStartTime && Date.now() - loadingStartTime > 10000 && !retryAttempt && (
              <p className="text-yellow-600 text-sm mt-3">
                <Clock className="w-4 h-4 inline-block mr-1" />
                Still loading... Large mailboxes may take up to 30 seconds
              </p>
            )}
          </div>
        ) : aiDraftConversations.length === 0 ? (
          <div className="p-8 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No email conversations found</p>
            <p className="text-gray-400 text-sm mt-1">
              Email conversations from your inbox will appear here
            </p>
          </div>
          ) : (
            <>
            <div className="divide-y divide-gray-100">
              {aiDraftConversations
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((conv) => (
                <div
                  key={conv.thread_id}
                  className={`p-4 hover:bg-yellow-50 transition-colors cursor-pointer ${
                    selectedAIDraft?.thread_id === conv.thread_id ? 'bg-yellow-50 border-l-4 border-yellow-500' : ''
                  }`}
                  onClick={() => handleSelectThread(conv)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                          <Mail className="w-6 h-6 text-yellow-600" />
                        </div>
                        {/* Show draft badge only when we have actual draft count data */}
                        {((conv.draft_count != null && conv.draft_count > 0) || conv.drafts.length > 0) && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                            <span className="text-[10px] font-bold text-white">{conv.draft_count ?? conv.drafts.length}</span>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900 truncate max-w-sm">
                            {conv.subject || '(No subject)'}
                          </p>
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                            {conv.emails.length} email{conv.emails.length !== 1 ? 's' : ''}
                          </span>
                          {((conv.draft_count != null && conv.draft_count > 0) || conv.drafts.length > 0) && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                              {conv.draft_count ?? conv.drafts.length} draft{(conv.draft_count ?? conv.drafts.length) !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">From:</span> {conv.customer_email || conv.emails[0]?.from || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-500 truncate max-w-md mt-1">
                          {conv.emails[0]?.snippet || conv.emails[0]?.body?.substring(0, 100) || 'No preview available'}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDateLocal(conv.last_updated || conv.emails[0]?.date)}
                          </span>
                          <span className="text-gray-300">|</span>
                          <span className="font-mono text-[10px] text-gray-400">
                            Thread: {conv.thread_id}
                          </span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls - Always show when there are conversations */}
            {aiDraftConversations.length > 0 && (
              <div className="p-4 border-t border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, aiDraftConversations.length)} of {aiDraftConversations.length} conversations
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.ceil(aiDraftConversations.length / itemsPerPage) }, (_, i) => i + 1)
                        .filter(page => {
                          const totalPages = Math.ceil(aiDraftConversations.length / itemsPerPage)
                          // Show first page, last page, current page, and pages around current
                          return page === 1 ||
                                 page === totalPages ||
                                 Math.abs(page - currentPage) <= 1
                        })
                        .map((page, idx, arr) => (
                          <div key={page} className="flex items-center gap-1">
                            {idx > 0 && arr[idx - 1] !== page - 1 && (
                              <span className="px-2 text-gray-400">...</span>
                            )}
                            <button
                              onClick={() => setCurrentPage(page)}
                              className={`w-8 h-8 text-sm rounded-lg ${
                                currentPage === page
                                  ? 'bg-yellow-500 text-white'
                                  : 'border border-gray-300 hover:bg-gray-100'
                              }`}
                            >
                              {page}
                            </button>
                          </div>
                        ))
                      }
                    </div>
                    <button
                      onClick={() => setCurrentPage(Math.min(Math.ceil(aiDraftConversations.length / itemsPerPage), currentPage + 1))}
                      disabled={currentPage >= Math.ceil(aiDraftConversations.length / itemsPerPage)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
            </>
          )}
      </div>

      {/* Conversation Detail Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold">
                  {selectedConversation.customer_name || selectedConversation.customer_email || 'Customer'}
                </h3>
                <p className="text-sm text-gray-500">{selectedConversation.customer_email}</p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden flex">
              {/* Messages Panel */}
              <div className="w-1/2 border-r border-gray-100 flex flex-col">
                <div className="p-4 border-b border-gray-100 bg-gray-50">
                  <h4 className="font-medium text-gray-900">Conversation</h4>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                    <span>Category: {selectedConversation.category}</span>
                    <span className={`px-2 py-0.5 rounded-full ${getStatusColor(selectedConversation.status)}`}>
                      {selectedConversation.status}
                    </span>
                  </div>
                  {(latestInboundEmail || latestDraftEmail) && (
                    <div className="mt-4 space-y-3 text-sm">
                      {latestInboundEmail && (
                        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                          <div className="flex items-center justify-between text-xs text-blue-700">
                            <span className="font-medium">Inbound Email</span>
                            {latestInboundEmail.metadata?.subject && (
                              <span className="truncate max-w-[220px]">
                                {latestInboundEmail.metadata.subject}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-blue-600 break-all">
                            Message ID: {latestInboundEmail.metadata?.email_id || latestInboundEmail.id}
                          </p>
                          <p className="mt-1 text-xs text-blue-600">
                            From: {latestInboundEmail.metadata?.from || 'Unknown'}
                          </p>
                          <p className="mt-2 whitespace-pre-wrap text-blue-900">
                            {latestInboundEmail.content}
                          </p>
                        </div>
                      )}
                      {latestDraftEmail && (
                        <div className="rounded-lg border border-purple-100 bg-purple-50 p-3">
                          <div className="flex items-center justify-between text-xs text-purple-700">
                            <span className="font-medium">Draft Reply</span>
                            {latestDraftEmail.metadata?.draft_id && (
                              <span className="truncate max-w-[220px]">
                                ID: {latestDraftEmail.metadata.draft_id}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-purple-600 break-all">
                            Draft Message ID: {latestDraftEmail.metadata?.draft_id || latestDraftEmail.id}
                          </p>
                          <p className="mt-1 text-xs text-purple-600">
                            To: {latestDraftEmail.metadata?.to || 'Unknown'}
                          </p>
                          <p className="mt-2 whitespace-pre-wrap text-purple-900">
                            {latestDraftEmail.content}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {isLoadingDetail ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-6 h-6 text-yellow-500 animate-spin" />
                    </div>
                  ) : conversationDetail?.messages.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No messages yet</p>
                  ) : (
                    conversationDetail?.messages.map((msg) => {
                      const isEmail = msg.metadata?.source === 'email'
                      const direction = msg.metadata?.direction
                      const subject = msg.metadata?.subject
                      const senderType = msg.metadata?.sender_type
                      const isAI = senderType === 'ai' || msg.metadata?.is_automated

                      return (
                        <div
                          key={msg.id}
                          className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg p-3 ${
                              msg.role === 'assistant'
                                ? isAI ? 'bg-purple-50 text-gray-900 border border-purple-200' : 'bg-gray-100 text-gray-900'
                                : 'bg-yellow-500 text-white'
                            }`}
                          >
                            {/* Email metadata header */}
                            {isEmail && (
                              <div className={`flex items-center gap-2 text-xs mb-2 pb-2 border-b ${
                                msg.role === 'assistant' ? 'border-gray-200 text-gray-500' : 'border-yellow-400 text-yellow-100'
                              }`}>
                                <Mail className="w-3 h-3" />
                                {direction === 'draft' ? (
                                  <span className="flex items-center gap-1">
                                    <FileText className="w-3 h-3" />
                                    Draft
                                  </span>
                                ) : direction === 'sent' ? (
                                  <span className="flex items-center gap-1">
                                    <ArrowUpRight className="w-3 h-3" />
                                    Sent
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <ArrowDownLeft className="w-3 h-3" />
                                    Received
                                  </span>
                                )}
                                {subject && <span className="truncate">| {subject}</span>}
                                {/* Sender type badge */}
                                {senderType && (
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    senderType === 'ai' ? 'bg-purple-200 text-purple-700' :
                                    senderType === 'system' ? 'bg-gray-200 text-gray-600' :
                                    senderType === 'agent' ? 'bg-blue-200 text-blue-700' :
                                    'bg-green-200 text-green-700'
                                  }`}>
                                    {senderType === 'ai' ? 'AI' :
                                     senderType === 'system' ? 'System' :
                                     senderType === 'agent' ? 'Agent' : 'Human'}
                                  </span>
                                )}
                              </div>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-xs mt-2 ${
                              msg.role === 'assistant' ? 'text-gray-500' : 'text-yellow-100'
                            }`}>
                              {msg.timestamp ? formatTime(msg.timestamp) : ''}
                              {msg.metadata?.original_date && ` (${msg.metadata.original_date})`}
                            </p>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>

              {/* Draft Panel */}
              <div className="w-1/2 flex flex-col">
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    AI Draft Response
                  </h4>
                  <div className="flex items-center gap-2">
                    {draft && (
                      <button
                        onClick={() => generateDraft(true)}
                        disabled={isGeneratingDraft}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                        title="Regenerate draft"
                      >
                        <RefreshCw className={`w-4 h-4 ${isGeneratingDraft ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {draftError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
                      {draftError}
                    </div>
                  )}

                  {!draft && !isGeneratingDraft ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <Sparkles className="w-12 h-12 text-yellow-500 mb-4" />
                      <p className="text-gray-500 mb-4">
                        Generate an AI-powered draft response using the knowledge base
                      </p>
                      <button
                        onClick={() => generateDraft(false)}
                        disabled={isGeneratingDraft}
                        className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                      >
                        <Sparkles className="w-4 h-4" />
                        Generate Draft
                      </button>
                    </div>
                  ) : isGeneratingDraft ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 text-yellow-500 animate-spin mb-4" />
                      <p className="text-gray-500 font-medium">Generating draft...</p>
                      <p className="text-sm text-gray-400 mt-1">Using AI + Knowledge Base</p>
                      <p className="text-xs text-gray-400 mt-3">This may take up to 1 minute</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Draft metadata */}
                      {draft?.metadata && !draft.metadata.manual && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>Model: {draft.metadata.model}</span>
                          {draft.metadata.latency_ms && (
                            <span>| {draft.metadata.latency_ms}ms</span>
                          )}
                        </div>
                      )}

                      {/* Draft content */}
                      {isEditing ? (
                        <textarea
                          value={draftContent}
                          onChange={(e) => setDraftContent(e.target.value)}
                          className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 resize-none"
                          placeholder="Edit your draft response..."
                        />
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-sm">
                          {draftContent}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Draft Actions */}
                {draft && (
                  <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={saveDraft}
                            disabled={isSavingDraft}
                            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
                          >
                            {isSavingDraft ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setDraftContent(draft.content)
                              setIsEditing(false)
                            }}
                            className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setIsEditing(true)}
                            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <Edit3 className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={copyToClipboard}
                            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            {copied ? (
                              <>
                                <Check className="w-4 h-4 text-green-600" />
                                <span className="text-green-600">Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4" />
                                Copy
                              </>
                            )}
                          </button>
                        </>
                      )}
                    </div>

                    {!isEditing && (
                      <button
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        onClick={() => {
                          // TODO: Implement send functionality
                          alert('Send functionality coming soon!')
                        }}
                      >
                        <Send className="w-4 h-4" />
                        Send Response
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
 
      {/* AI-Draft Detail Modal */}
      {selectedAIDraft && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedAIDraft(null)}
        >
          <div
            className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex-shrink-0 bg-purple-50">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h3 className="text-lg font-semibold text-purple-900">
                      AI-Draft Email Thread
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{selectedAIDraft.subject || '(No subject)'}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Customer: {selectedAIDraft.customer_email || 'Unknown'} | Thread: {selectedAIDraft.thread_id}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedAIDraft(null)}
                  className="p-2 hover:bg-purple-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Agent Assignment & Actions */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Agent Badge - show loading state while fetching details */}
                {isLoadingThreadDetails ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-lg">
                    <Loader2 className="w-4 h-4 text-gray-600 animate-spin" />
                    <span className="text-sm font-medium text-gray-700">
                      Loading...
                    </span>
                  </div>
                ) : selectedAIDraft.assigned_agent_name ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg">
                    <Bot className="w-4 h-4 text-green-700" />
                    <span className="text-sm font-medium text-green-900">
                      {selectedAIDraft.assigned_agent_name}
                    </span>
                    <Tag className="w-3 h-3 text-green-600" />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 border border-yellow-300 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-yellow-700" />
                    <span className="text-sm font-medium text-yellow-900">
                      Not Classified
                    </span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2 ml-auto">
                  {/* Classify Button - hide while loading thread details */}
                  {!isLoadingThreadDetails && !selectedAIDraft.assigned_agent_id && (
                    <button
                      onClick={() => classifyEmailWithRouter(selectedAIDraft.thread_id)}
                      disabled={isClassifying}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isClassifying ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Classifying...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4" />
                          Classify with AI Router
                        </>
                      )}
                    </button>
                  )}

                  {/* Generate Response Button - hide for manual agents and while loading */}
                  {!isLoadingThreadDetails && selectedAIDraft.assigned_agent_id && (
                    selectedAIDraft.response_mode === 'manual' ? (
                      <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm">
                        <Edit3 className="w-4 h-4" />
                        Manual Response Required
                      </div>
                    ) : selectedAIDraft.draft_response_enabled === false ? (
                      <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm">
                        Draft Response Disabled
                      </div>
                    ) : (
                      <button
                        onClick={() => generateAIResponse(selectedAIDraft.thread_id, selectedAIDraft.assigned_agent_id!)}
                        disabled={isGeneratingAIResponse}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isGeneratingAIResponse ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            Generate AI Response
                          </>
                        )}
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Errors */}
              {classifyError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-700">{classifyError}</span>
                </div>
              )}
              {aiResponseError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-700">{aiResponseError}</span>
                </div>
              )}
              {sendDraftError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-700">{sendDraftError}</span>
                </div>
              )}
              {sendDraftSuccess && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-700">{sendDraftSuccess}</span>
                </div>
              )}
            </div>
 
            {/* Content - Split View */}
            <div className="flex-1 overflow-hidden flex">
              {/* Original Emails Panel */}
              <div className="w-1/2 border-r border-gray-100 flex flex-col">
                <div className="p-4 border-b border-gray-100 bg-blue-50">
                  <h4 className="font-medium text-blue-900 flex items-center gap-2">
                    <ArrowDownLeft className="w-4 h-4" />
                    Original Emails ({selectedAIDraft.emails.length})
                  </h4>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {selectedAIDraft.emails.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No emails in this thread</p>
                  ) : (
                    selectedAIDraft.emails.map((email, idx) => (
                      <div
                        key={email.id || idx}
                        className={`rounded-lg border p-4 ${
                          email.direction === 'received'
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            email.direction === 'received'
                              ? 'bg-blue-200 text-blue-800'
                              : 'bg-gray-200 text-gray-700'
                          }`}>
                            {email.direction === 'received' ? 'Received' : 'Sent'}
                          </span>
                          <span className="text-xs text-gray-500">{formatDateLocal(email.date)}</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <p><span className="font-medium text-gray-700">From:</span> <span className="text-gray-600">{email.from}</span></p>
                          <p><span className="font-medium text-gray-700">To:</span> <span className="text-gray-600">{email.to}</span></p>
                          <p><span className="font-medium text-gray-700">Subject:</span> <span className="text-gray-600">{email.subject}</span></p>
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-500 mb-2">Message Content:</p>
                          <div className="bg-white rounded p-3 text-sm text-gray-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
                            {email.body || email.snippet || '(No content)'}
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-400 break-all">
                          Message ID: {email.message_id || email.id}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
 
              {/* AI Draft Responses Panel */}
              <div className="w-1/2 flex flex-col">
                <div className="p-4 border-b border-gray-100 bg-green-50">
                  <h4 className="font-medium text-green-900 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    AI Draft Responses ({isLoadingThreadDetails ? '...' : selectedAIDraft.drafts.length})
                  </h4>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {isLoadingThreadDetails ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <Loader2 className="w-12 h-12 text-green-400 mb-4 animate-spin" />
                      <p className="text-gray-500 font-medium">Loading drafts...</p>
                    </div>
                  ) : selectedAIDraft.drafts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center px-4">
                      <FileText className="w-12 h-12 text-gray-300 mb-4" />
                      <p className="text-gray-500 font-medium">No AI drafts for this thread</p>
                      {!selectedAIDraft.assigned_agent_id ? (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg max-w-md">
                          <p className="text-sm text-yellow-800 font-medium mb-1">⚠️ Classification Required</p>
                          <p className="text-xs text-yellow-700">
                            This thread must be classified with AI Router before generating draft responses.
                            Click "Classify with AI Router" above to get started.
                          </p>
                        </div>
                      ) : (
                        <p className="text-gray-400 text-sm mt-1">
                          Click "Generate AI Response" above to create a draft
                        </p>
                      )}
                    </div>
                  ) : (
                    selectedAIDraft.drafts.map((draft, idx) => {
                      const isEditing = editingDraftId === draft.draft_id
                      return (
                        <div
                          key={draft.draft_id || idx}
                          className="rounded-lg border border-green-200 bg-green-50 p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-200 text-green-800">
                              AI Draft #{idx + 1}
                            </span>
                            <span className="text-xs text-gray-500">{formatDateLocal(draft.date)}</span>
                          </div>

                          {aiDraftSaveError && isEditing && (
                            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                              {aiDraftSaveError}
                            </div>
                          )}

                          <div className="space-y-2 text-sm">
                            <p><span className="font-medium text-gray-700">To:</span> <span className="text-gray-600">{draft.to}</span></p>
                            {isEditing ? (
                              <div>
                                <label className="block font-medium text-gray-700 mb-1">Subject:</label>
                                <input
                                  type="text"
                                  value={editedDraftContent.subject}
                                  onChange={(e) => setEditedDraftContent({ ...editedDraftContent, subject: e.target.value })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500"
                                />
                              </div>
                            ) : (
                              <p><span className="font-medium text-gray-700">Subject:</span> <span className="text-gray-600">{draft.subject}</span></p>
                            )}
                          </div>
                          <div className="mt-4 pt-4 border-t border-green-200">
                            <p className="text-xs font-medium text-gray-500 mb-2">Draft Content:</p>
                            {isEditing ? (
                              <textarea
                                value={editedDraftContent.body}
                                onChange={(e) => setEditedDraftContent({ ...editedDraftContent, body: e.target.value })}
                                className="w-full h-48 px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 resize-none"
                              />
                            ) : (
                              <div className="bg-white rounded p-3 text-sm text-gray-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
                                {draft.body || draft.snippet || '(No content)'}
                              </div>
                            )}
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="text-xs text-gray-400">
                              Draft ID: {draft.draft_id}
                            </span>
                            <div className="flex items-center gap-2">
                              {isEditing ? (
                                <>
                                  <button
                                    onClick={() => saveEditedAIDraft(draft.draft_id, selectedAIDraft.thread_id)}
                                    disabled={isSavingAIDraft}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white hover:bg-green-700 rounded transition-colors disabled:opacity-50"
                                  >
                                    {isSavingAIDraft ? (
                                      <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Saving...
                                      </>
                                    ) : (
                                      <>
                                        <Check className="w-3 h-3" />
                                        Save Version
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingDraftId(null)
                                      setEditedDraftContent({ subject: '', body: '' })
                                      setAIDraftSaveError(null)
                                    }}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-200 rounded transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => {
                                      setEditingDraftId(draft.draft_id)
                                      setEditedDraftContent({ subject: draft.subject, body: draft.body || '' })
                                      setAIDraftSaveError(null)
                                    }}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100 rounded transition-colors"
                                  >
                                    <Edit3 className="w-3 h-3" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(draft.body || '')
                                      setCopied(true)
                                      setTimeout(() => setCopied(false), 2000)
                                    }}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-green-700 hover:bg-green-100 rounded transition-colors"
                                  >
                                    {copied ? (
                                      <>
                                        <Check className="w-3 h-3" />
                                        Copied!
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="w-3 h-3" />
                                        Copy
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => sendDraftResponse(draft.draft_id, selectedAIDraft.thread_id, idx + 1)}
                                    disabled={sendingDraftId === draft.draft_id}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-purple-700 hover:bg-purple-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {sendingDraftId === draft.draft_id ? (
                                      <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Sending...
                                      </>
                                    ) : (
                                      <>
                                        <Send className="w-3 h-3" />
                                        Send
                                      </>
                                    )}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Classify All Confirmation Modal */}
      {showClassifyConfirm && (() => {
        const unclassifiedCount = aiDraftConversations.filter(c => !c.assigned_agent_id).length
        const totalCount = aiDraftConversations.length
        const isReclassify = unclassifiedCount === 0

        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Zap className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {isReclassify ? 'Confirm Reclassification' : 'Confirm Bulk Classification'}
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                {isReclassify ? (
                  <>
                    All conversations are already classified. Proceed to{' '}
                    <span className="font-bold text-blue-600">
                      reclassify all {totalCount} conversations
                    </span>{' '}
                    with AI Router?
                  </>
                ) : (
                  <>
                    Proceed to classify{' '}
                    <span className="font-bold text-blue-600">
                      {unclassifiedCount} unclassified conversations
                    </span>{' '}
                    with AI Router?
                  </>
                )}
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowClassifyConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowClassifyConfirm(false)
                    classifyAllUnclassified()
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {isReclassify ? 'Reclassify All' : 'Classify All'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Generate All Drafts Confirmation Modal */}
      {showDraftsConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Bulk Draft Generation</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Proceed to generate{' '}
              <span className="font-bold text-purple-600">
                {aiDraftConversations.filter(c => c.assigned_agent_id).length} AI draft responses
              </span>{' '}
              for all classified threads?
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowDraftsConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDraftsConfirm(false)
                  generateAllDrafts()
                }}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Generate All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
